import { StateCreator } from "zustand";
import type {
  MeetingDetails,
  RoomMember,
  VideoRoomAccessResponse,
} from "./videoMeeting.types";

// --- State Type ---
export type VideoMeetingSliceState = {
  meetingId: number | null;
  roomId: number | null;
  chimeExternalUserId: string | null;
  meetingDetails: MeetingDetails | null;
  roomAccess: VideoRoomAccessResponse | null;
  isVerifyingRoomAccess: boolean;
  roomMembersData: Record<number, RoomMember>;
  transcriptionStatus: "on" | "off";
};

// --- Actions Type ---
export type VideoMeetingSliceActions = {
  setMeetingId: (meetingId: number | null) => void;
  setRoomId: (roomId: number | null) => void;
  setChimeExternalUserId: (id: string | null) => void;
  setMeetingDetails: (details: MeetingDetails | null) => void;
  setRoomAccess: (access: VideoRoomAccessResponse | null) => void;
  setVerifyingRoomAccess: (isVerifying: boolean) => void;
  setRoomMembersData: (members: RoomMember[]) => void;
  setTranscriptionStatus: (status: "on" | "off") => void;
  resetVideoMeeting: () => void;
};

// --- Combined Type ---
export type VideoMeetingSlice = VideoMeetingSliceState &
  VideoMeetingSliceActions;

// --- Initial State ---
export const initialVideoMeetingState: VideoMeetingSliceState = {
  meetingId: null,
  roomId: null,
  chimeExternalUserId: null,
  meetingDetails: null,
  roomAccess: null,
  isVerifyingRoomAccess: false,
  roomMembersData: {},
  transcriptionStatus: "off",
};

// --- Slice Creator ---
export const createVideoMeetingSlice: StateCreator<
  VideoMeetingSlice,
  [],
  [],
  VideoMeetingSlice
> = (set) => ({
  ...initialVideoMeetingState,

  setMeetingId: (meetingId) => set({ meetingId }),

  setRoomId: (roomId) => set({ roomId }),

  setChimeExternalUserId: (id) => set({ chimeExternalUserId: id }),

  setMeetingDetails: (details) => set({ meetingDetails: details }),

  setRoomAccess: (access) =>
    set({ roomAccess: access, isVerifyingRoomAccess: false }),

  setVerifyingRoomAccess: (isVerifying) =>
    set({ isVerifyingRoomAccess: isVerifying }),

  setRoomMembersData: (members) => {
    const map: Record<number, RoomMember> = {};
    members.forEach((m) => {
      map[m.user_id] = m;
    });
    set({ roomMembersData: map });
  },

  setTranscriptionStatus: (status) => set({ transcriptionStatus: status }),

  resetVideoMeeting: () => set(initialVideoMeetingState),
});

// --- Selectors ---
export const videoMeeting = {
  details: (s: VideoMeetingSlice) => ({
    meetingId: s.meetingId,
    roomId: s.roomId,
    chimeExternalUserId: s.chimeExternalUserId,
    meetingDetails: s.meetingDetails,
    setMeetingId: s.setMeetingId,
    setRoomId: s.setRoomId,
    setChimeExternalUserId: s.setChimeExternalUserId,
    setMeetingDetails: s.setMeetingDetails,
  }),
  access: (s: VideoMeetingSlice) => ({
    roomAccess: s.roomAccess,
    isVerifying: s.isVerifyingRoomAccess,
    setRoomAccess: s.setRoomAccess,
    setVerifying: s.setVerifyingRoomAccess,
  }),
  members: (s: VideoMeetingSlice) => ({
    roomMembersData: s.roomMembersData,
    setRoomMembersData: s.setRoomMembersData,
  }),
  transcription: (s: VideoMeetingSlice) => ({
    transcriptionStatus: s.transcriptionStatus,
    setTranscriptionStatus: s.setTranscriptionStatus,
  }),
  reset: (s: VideoMeetingSlice) => ({
    resetVideoMeeting: s.resetVideoMeeting,
  }),
};
